// The One-Line's screen (MOCKUP_IEA_ONE_LINE.html rev 3, approved by Robert 2026-10-21). Symbols drawn the way SKM
// prints them: open terminal circles, the utility funnel, transformer coils (delta primary, grounded wye secondary),
// the breaker arc, the fuse box, the cable coil, the VFD box. Every item carries its SKM name: the tag, then the Asset
// ID on the admin card. Read only. It sits on the Drawing's paper, so pan, pinch and Fit work the same way.
//
// ONE_LINE_UNFOLD_WORDS.md (Robert, approved 2026-10-30 9:05 AM): the face shows only each device's label name. A tap
// unfolds a card at the device with what is known, in field order, up to and including incident energy and voltage.
// The same screen serves the QEW site layout (admin false): no SKM names, no Open Card, the fixed safety line on top.

import { esc } from "./training-screens";
import type { DrawView } from "./survey-drawing-screens";
import { viewTransform } from "./survey-drawing-screens";
import { modelCheck, type OlFeed, type OlNode, type OneLine } from "./survey-oneline";

export interface OneLineScreen {
  ol: OneLine;
  view: DrawView;
  /** The item unfolded: "n:{Asset ID}", or "cbl:{link}" for a line. */
  sel: string | null;
  /** Asset IDs show without the site number, as on SKM names. */
  short: (assetId: string) => string;
  /** What is known about a device, for its card. */
  detail: (assetId: string) => OlDetail;
  /** Admin: SKM names, the model check, Open Card and Open Wire Card. False on the QEW site layout. */
  admin: boolean;
}

/** A device's card, field order (words section 2). Published study values only; "Waits on the study" until then. */
export interface OlDetail {
  type: string;
  room: string;
  volts: string;
  ie: string;
  afb: string;
  overduty: boolean;
  ieStatus: string;
  limited: string;
  restricted: string;
  fedFrom: string | null;
  fedNotVerified: boolean;
  study: string;
  assetHref: string;
}

/** The fixed line on the QEW layout (words section 3, Robert 9:05 AM). */
export const QEW_LAYOUT_LINE = "Layout for finding your way. Not an isolation drawing. Verify the absence of voltage before you touch it.";

export function oneLineScreen(v: OneLineScreen): string {
  const left = v.admin ? checkHtml(v) : `<div class="ol-qew">${esc(QEW_LAYOUT_LINE)}</div>`;
  const hint = `<div class="dw-hint">Tap a device to open what is known about it. Tap it again to fold it. Tap a line to see where it comes from.${v.admin ? " The One-Line reads the Drawing: arrange it there." : ""}</div>`;
  return `<div class="dw-wrap ol-wrap"><div class="dw-left" id="dw-left">${left}${hint}</div>
    <div class="dw-main"><div class="dw-tools"><button type="button" class="sv-btn line sm" data-dw="fit">Fit</button></div>
    <div class="dw-paper"><svg id="dw-svg" class="dw-svg ol-svg" xmlns="http://www.w3.org/2000/svg" aria-label="One-Line">${defs()}<g id="dw-g" transform="${viewTransform(v.view)}">${paperHtml(v)}</g></svg></div></div></div>`;
}

function defs(): string {
  return `<defs><pattern id="ol-grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f1f3f5" stroke-width="1"/></pattern></defs>`;
}

function paperHtml(v: OneLineScreen): string {
  let s = `<rect x="-20000" y="-20000" width="40000" height="40000" fill="url(#ol-grid)" data-dw="ol-paper"/>`;
  for (const f of v.ol.feeds) s += feedSvg(v, f);
  for (const n of v.ol.nodes) s += nodeSvg(v, n);
  // The unfolded card goes last, so it sits over everything near it.
  return s + popHtml(v);
}

const term = (x: number, y: number) => `<circle cx="${x}" cy="${y}" r="2.6" class="ol-term"/>`;
const vline = (x: number, y1: number, y2: number, nv = false) => `<line x1="${x}" y1="${y1}" x2="${x}" y2="${y2}" class="ol-ln${nv ? " nv" : ""}"/>`;
const text = (x: number, y: number, s: string, cls: string) => `<text x="${x}" y="${y}" class="${cls}">${esc(s)}</text>`;

function item(key: string, picked: boolean, box: [number, number, number, number], body: string): string {
  const [x, y, w, h] = box;
  return `<g class="ol-it" data-dw="ol" data-dw-v="${esc(key)}">${picked ? `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="6" class="ol-sel"/>` : ""}${body}<rect x="${x}" y="${y}" width="${w}" height="${h}" class="ol-hit"/></g>`;
}

function nodeSvg(v: OneLineScreen, n: OlNode): string {
  const key = `n:${n.assetId}`;
  const picked = v.sel === key;
  const x = n.cx;
  const y = n.y;
  if (n.kind === "bus") {
    const w = Math.max(n.x1 - n.x0, 60);
    // The label name, the same as the yellow tape (words section 1), in the first spot where it crosses no line.
    const into = v.ol.feeds.some((f) => f.to === n.assetId) ? n.cx : null;
    const outs = v.ol.feeds.filter((f) => f.from === n.assetId).map((f) => f.x1);
    const p = busNamePlace(n.x0, n.x0 + w, y, n.name, into, outs);
    const body = `<line x1="${n.x0}" y1="${y}" x2="${n.x0 + w}" y2="${y}" class="ol-busbar"/>${text(p.x, p.y, n.name, "ol-name")}`;
    return item(key, picked, [n.x0 - 6, y - 20, w + 12, 28], body);
  }
  let sym = "";
  if (n.kind === "utility") {
    // The utility funnel, then the service transformer's coils (the utility asset carries both tags).
    sym = `<path d="M${x - 12} ${y - 46} L${x + 12} ${y - 46} L${x} ${y - 30} Z" class="ol-solid"/>${vline(x, y - 30, y - 22)}${coils(x, y)}`;
  } else if (n.kind === "transformer") sym = coils(x, y);
  else if (n.kind === "generator") sym = `${term(x, y - 22)}<circle cx="${x}" cy="${y}" r="16" class="ol-sym"/><text x="${x}" y="${y + 5}" class="ol-mid">G</text>${term(x, y + 22)}`;
  else if (n.kind === "vfd") sym = `${term(x, y - 22)}<rect x="${x - 13}" y="${y - 16}" width="26" height="32" class="ol-sym"/><path d="M${x - 13} ${y + 16} L${x + 13} ${y - 16} M${x - 10} ${y - 7} q3 -4 6 0 t6 0 M${x - 3} ${y + 8} q3 -4 6 0 t6 0" class="ol-ln"/>${term(x, y + 22)}`;
  else sym = `${term(x - 10, y - 22)}${term(x + 10, y - 22)}<path d="M${x} ${y + 16} L${x - 9} ${y - 18}" class="ol-ln"/>${term(x, y + 18)}<text x="${x + 12}" y="${y + 4}" class="ol-sm">ATS</text>`;
  const top = n.kind === "utility" ? y - 50 : y - 26;
  return item(key, picked, [x - 26, top, 170, n.bottom - top + 6], sym + text(x + nameDx(n.kind), y + 5, n.name, "ol-name"));
}

/** How far right of a symbol's center line its name starts: clear of the symbol, and of the "ATS" letters. */
export function nameDx(kind: OlNode["kind"]): number {
  return kind === "ats" ? 44 : 24;
}

/** About how wide a name draws at 13 px bold, for keeping it off the lines. */
export const nameWidth = (name: string) => Math.ceil(name.length * 8.4) + 4;

/**
 * Where a bus's name goes so it crosses no line and covers no symbol (Robert 2026-10-30 9:18 AM: "it needs to be
 * clean"). Above the bar, left of the line coming in; else above, right of it; else below, left of the first line
 * going out; else past the right end of the bar. `into` is the x of the line coming in, `outs` the lines going out.
 */
export function busNamePlace(x0: number, x1: number, y: number, name: string, into: number | null, outs: readonly number[]): { x: number; y: number } {
  const w = nameWidth(name);
  const gap = 14; // clears the device symbol on the line, not just the line
  const above = y - 8;
  const below = y + 17;
  if (into === null || x0 + w <= into - gap) return { x: x0, y: above };
  if (into + gap + w <= x1 + 20) return { x: into + gap, y: above };
  const firstOut = outs.length ? Math.min(...outs) - 12 : Infinity;
  if (x0 + w <= firstOut - gap) return { x: x0, y: below };
  return { x: x1 + 8, y: y + 5 };
}

/** Transformer coils (mockup rev 3): primary humps down, secondary up, delta primary, grounded wye secondary. */
function coils(x: number, y: number): string {
  return `${term(x, y - 22)}${vline(x, y - 19, y - 12)}<path d="M${x - 12} ${y - 10} a4 4 0 0 0 8 0 a4 4 0 0 0 8 0 a4 4 0 0 0 8 0" class="ol-ln"/><path d="M${x - 12} ${y + 4} a4 4 0 0 1 8 0 a4 4 0 0 1 8 0 a4 4 0 0 1 8 0" class="ol-ln"/>
    <path d="M${x - 30} ${y - 6} l5 -9 l5 9 z" class="ol-ln"/><path d="M${x - 25} ${y + 2} v5 M${x - 25} ${y + 7} l-4 4 M${x - 25} ${y + 7} l4 4 M${x - 25} ${y + 7} v6 M${x - 30} ${y + 13} h10 M${x - 28} ${y + 16} h6" class="ol-ln"/>
    ${vline(x, y + 4, y + 19)}${term(x, y + 22)}`;
}

function feedSvg(v: OneLineScreen, f: OlFeed): string {
  const nv = f.notVerified;
  let s = "";
  let yTop = f.y1;
  if (f.pd) {
    // The protective device on the position: breaker arc, or fuse box.
    const y = f.pd.y;
    const sym = f.pd.fuse
      ? `${vline(f.x1, yTop, y + 4)}<rect x="${f.x1 - 4}" y="${y + 4}" width="8" height="16" class="ol-sym"/>${vline(f.x1, y + 4, y + 20)}${vline(f.x1, y + 20, y + 30)}`
      : `${vline(f.x1, yTop, y + 5)}${term(f.x1, y + 5)}<path d="M${f.x1} ${y + 8} c7 2 7 8 0 10" class="ol-ln"/>${term(f.x1, y + 21)}${vline(f.x1, y + 24, y + 30)}`;
    // No text on the line (words section 1): a tap on the device opens the line's card.
    s += item(`cbl:${f.id}`, v.sel === `cbl:${f.id}`, [f.x1 - 12, y - 4, 24, 36], sym);
    yTop = y + 30;
  }
  s += `<path d="M${f.x1} ${yTop} V${f.bus} H${f.x2} V${f.cable.y - 14}" class="ol-ln${nv ? " nv" : ""}"/>`;
  // The cable into the gear: a coil between two terminals, its tag and size beside it.
  const m = f.cable.y;
  const coil = `${term(f.x2, m - 14)}<path d="M${f.x2} ${m - 11} c-7 1 -7 5 0 5 c-7 1 -7 5 0 5 c-7 1 -7 5 0 5 c-7 1 -7 5 0 5" class="ol-ln"/>${term(f.x2, m + 11)}${vline(f.x2, m + 14, f.y2, nv)}`;
  s += item(`cbl:${f.id}`, v.sel === `cbl:${f.id}`, [f.x2 - 12, m - 18, 24, 38], coil);
  // P sits left of its line, E right of its line: each tag on its own outside, never on the other line.
  if (f.side) {
    const tx = f.side === "E" ? f.x2 + 10 : f.x2 - 30;
    s += `<g class="dw-side"><rect x="${tx}" y="${f.y2 - 24}" width="20" height="18" rx="3"/><text x="${tx + 10}" y="${f.y2 - 11}">${f.side}</text></g>`;
  }
  return s;
}


const row = (k: string, val: string) => `<div class="ol-prow"><span>${esc(k)}</span><b>${val}</b></div>`;

/**
 * The card that unfolds at the device or the line (words section 2). It is drawn at screen size whatever the zoom, so
 * it reads the same zoomed out to the whole site or in on one room.
 */
function popHtml(v: OneLineScreen): string {
  const sel = v.sel;
  if (!sel) return "";
  const kind = sel.slice(0, sel.indexOf(":"));
  const ref = sel.slice(sel.indexOf(":") + 1);
  const k = v.view.k || 1;
  const at = (x: number, y: number, body: string) =>
    `<g transform="translate(${x} ${y}) scale(${Math.round((1 / k) * 1000) / 1000})"><foreignObject width="300" height="520" class="ol-fo"><div xmlns="http://www.w3.org/1999/xhtml" class="ol-pop">${body}</div></foreignObject></g>`;
  if (kind === "n") {
    const n = v.ol.nodes.find((x) => x.assetId === ref);
    if (!n) return "";
    const d = v.detail(n.assetId);
    const id = v.short(n.assetId);
    const ie = d.overduty ? `<span class="ol-od">OVERDUTY</span>` : esc(d.ie);
    const body = `<div class="ol-phd"><span class="fl-lab sm">${esc(n.name)}</span><button type="button" class="ol-x" data-dw="ol" data-dw-v="${esc(sel)}" aria-label="Fold">Close</button></div>
      <div class="ol-psub">${esc([d.type, d.room].filter(Boolean).join(" · "))}</div>
      ${row("Voltage", esc(d.volts))}${row("Incident Energy", ie)}${d.overduty && d.ieStatus ? `<div class="ol-odline">${esc(d.ieStatus)}</div>` : ""}${row("Arc Flash Boundary", esc(d.afb))}
      ${row("Limited Approach", esc(d.limited))}${row("Restricted Approach", esc(d.restricted))}
      ${row("Fed From", esc(d.fedFrom ?? "Not connected"))}${d.fedFrom && d.fedNotVerified ? `<div class="ol-nvline">Not verified in the field</div>` : ""}
      ${row("Study", esc(d.study))}${v.admin ? row("SKM Name", esc(n.tag ? `${n.tag} (${id})` : "No tag yet")) : ""}
      <a class="sv-btn gold ol-pbtn" href="${esc(d.assetHref)}">Open Asset Page</a>${v.admin ? `<button type="button" class="sv-btn line ol-pbtn" data-dw="open-card" data-dw-v="${esc(n.assetId)}">Open Card</button>` : ""}`;
    const ax = n.kind === "bus" ? Math.max(n.x1, n.x0 + 60) + 12 : n.cx + 34;
    return at(ax, n.top - 10, body);
  }
  const f = v.ol.feeds.find((x) => x.id === ref);
  if (!f) return "";
  const from = v.ol.nodes.find((x) => x.assetId === f.from);
  const to = v.ol.nodes.find((x) => x.assetId === f.to);
  const conn = f.notVerified ? `<span class="ol-nvline in">Not verified in the field</span>` : "Verified";
  const body = `<div class="ol-phd"><b class="ol-ptl">${esc(to?.name ?? v.short(f.to))}</b><button type="button" class="ol-x" data-dw="ol" data-dw-v="${esc(sel)}" aria-label="Fold">Close</button></div>
    <div class="ol-psub">Fed from ${esc((from?.name ?? v.short(f.from)) + (f.point ? ` ${f.point}` : ""))}${f.side ? ` · ${f.side === "P" ? "Primary" : "Emergency"}` : ""}</div>
    ${row("Connection", conn)}${f.pd ? row("Device", esc(f.pd.data || "Not filled")) : ""}${row("Wire", esc(f.cable.data || "Not filled"))}
    ${v.admin ? `${f.pd ? row("Device SKM", esc(f.pd.tag ?? "No tag yet")) : ""}${row("Cable SKM", esc(f.cable.tag ?? "No tag yet"))}<button type="button" class="sv-btn line ol-pbtn" data-dw="line" data-dw-v="${esc(f.id)}">Open Wire Card</button>` : ""}`;
  return at(f.x2 + 18, f.cable.y - 20, body);
}

function checkHtml(v: OneLineScreen): string {
  const c = modelCheck(v.ol);
  const quiet = !c.notVerified && !c.untagged;
  return `<div class="ol-check${quiet ? " ok" : ""}">Model check · <b>${c.items}</b> items for KE<br><b>${c.notVerified}</b> ${c.notVerified === 1 ? "connection" : "connections"} Not Verified${c.untagged ? ` · <b>${c.untagged}</b> without a tag yet` : ""}</div>`;
}
